import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EVERYONE_SENTINEL } from "@sp-crm/core";
import { clearBoundsFromGeoParams } from "components/clients/client-map-view/geo-params";
import { ClientMapSearchType } from "components/clients/client-map-view/search-input";
import {
    ClientStatusFilterValue,
    defaultClientStatusFilterValue,
} from "components/clients/client-map-view/status-groups";
import { SelectableUser } from "components/shared/user-select";
import { CoordinatesInput, SearchGeoParams } from "generated/graphql";

interface SetExplicitMapBounds {
    northWest: CoordinatesInput;
    southEast: CoordinatesInput;
}

export interface ClientMapViewState {
    searchText: string;
    searchType: ClientMapSearchType;
    assignedUserId: SelectableUser;
    statusFilter: ClientStatusFilterValue;
    geoParams: SearchGeoParams | null;
    page: number;
    perPage: number;
}

const defaultPerPage = 20;

export const initialClientMapViewState: ClientMapViewState = {
    searchText: "",
    searchType: "bestName",
    assignedUserId: EVERYONE_SENTINEL,
    statusFilter: defaultClientStatusFilterValue,
    geoParams: null,
    page: 0,
    perPage: defaultPerPage,
};

export const clientMapViewSlice = createSlice({
    name: "clientMapView",
    initialState: initialClientMapViewState,
    reducers: {
        updateSearchText: (state, action: PayloadAction<string>) => {
            state.searchText = action.payload;
            state.page = 0;
        },
        updateSearchType: (state, action: PayloadAction<ClientMapSearchType>) => {
            state.searchType = action.payload;
            state.page = 0;
        },
        updateAssignedUserId: (state, action: PayloadAction<SelectableUser>) => {
            state.assignedUserId = action.payload;
            state.page = 0;
        },
        updateStatusFilter: (state, action: PayloadAction<ClientStatusFilterValue>) => {
            state.statusFilter = action.payload;
            state.page = 0;
        },
        updateGeoParams: (state, action: PayloadAction<SearchGeoParams | null>) => {
            state.geoParams = action.payload;
            state.page = 0;
        },
        setExplicitMapBounds: (state, action: PayloadAction<SetExplicitMapBounds>) => {
            state.geoParams = {
                ...(state.geoParams ?? {}),
                bounds: {
                    northWest: action.payload.northWest,
                    southEast: action.payload.southEast,
                },
            };
            state.page = 0;
        },
        resetMapBounds: state => {
            state.geoParams = clearBoundsFromGeoParams(state.geoParams);
            state.page = 0;
        },
        changePage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        changePageSize: (state, action: PayloadAction<number>) => {
            state.perPage = action.payload;
        },
        clearAllFilters: state => {
            state.searchText = "";
            state.assignedUserId = EVERYONE_SENTINEL;
            state.statusFilter = defaultClientStatusFilterValue;
            state.geoParams = null;
            state.page = 0;
        },
    },
});
